import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Info, Search, ShieldCheck, Users } from 'lucide-react';

import { request } from '@/api/client';
import type {
  ActivityBand,
  AdminTeam,
  Page,
  Person,
  PersonRole,
  SubscriptionStatus,
} from '@/api/types';
import { ActivityBadge, ActivityLegend } from '@/components/ActivityBadge';
import { PageHeader } from '@/components/AppShell';
import { DataTable, PageSizeSelect, Pagination, type Column } from '@/components/DataTable';
import { usePageSize } from '@/lib/pageSize';
import { useToast } from '@/components/Toast';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Field,
  Modal,
  Select,
  TableSkeleton,
  TextInput,
  Toggle,
} from '@/components/ui';
import { useAuth } from '@/auth/AuthContext';
import { count, countryFlag, countryName, relativeDays } from '@/lib/format';
import { useDebounced, useResource } from '@/lib/hooks';
import { useDataScope } from '@/lib/dataPurpose';
import { PurposeBadge, PurposeEditor } from '@/components/DataPurpose';
import { personDetailUrl, rememberListScroll, useRestoreListScroll } from '@/lib/personDetailLink';
import { useLocation } from 'react-router-dom';


export function PeoplePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const { account } = useAuth();
  const { scope } = useDataScope();
  const [purposeEditing, setPurposeEditing] = useState<Person | null>(null);

  // Applied filters live in the URL so a sidebar link, browser Back, and a
  // copied link all render the same result set.
  const roleValue = searchParams.get('role');
  const role: PersonRole | 'all' = roleValue === 'athlete' || roleValue === 'coach' || roleValue === 'parent'
    ? roleValue : 'all';
  const search = Array.from(searchParams.get('search') ?? '').slice(0, 120).join('');
  const personId = searchParams.get('person_id') ?? '';
  const requestedReturn = searchParams.get('return_to');
  const returnTo = requestedReturn && /^\/clubs\/[A-Za-z0-9-]+(?:\?[^#\\]*)?$/.test(requestedReturn)
    ? requestedReturn : null;
  const returnLabel = searchParams.get('club_name') || t('clubs.name');
  const activityValue = searchParams.get('activity');
  const activity: ActivityBand | '' = activityValue === 'active' || activityValue === 'lapsing' ||
    activityValue === 'dormant' || activityValue === 'unknown' ? activityValue : '';
  const pageValue = Number(searchParams.get('page'));
  const page = Number.isSafeInteger(pageValue) && pageValue > 0 ? pageValue : 1;
  const registeredFrom = searchParams.get('registered_from') ?? '';
  const registeredTo = searchParams.get('registered_to') ?? '';
  const [draftFrom, setDraftFrom] = useState(registeredFrom);
  const [draftTo, setDraftTo] = useState(registeredTo);
  const [editing, setEditing] = useState<Person | null>(null);
  const [assigning, setAssigning] = useState<Person | null>(null);
  const [subscribing, setSubscribing] = useState<Person | null>(null);
  const [closing, setClosing] = useState<Person | null>(null);
  const [promoting, setPromoting] = useState<Person | null>(null);
  const [typedEmail, setTypedEmail] = useState('');
  const requestedSort = searchParams.get('sort');
  const sort = requestedSort && new Set(['name', 'email', 'role', 'created_at', 'last_seen_at', 'real_name', 'gender', 'birth_date', 'position', 'country', 'club', 'status']).has(requestedSort) ? requestedSort : 'created_at';
  const order = searchParams.get('order') === 'asc' ? 'asc' : 'desc';
  const debouncedSearch = useDebounced(search);
  const [pageSize, setPageSize] = usePageSize('people');

  useEffect(() => {
    setDraftFrom(registeredFrom);
    setDraftTo(registeredTo);
  }, [registeredFrom, registeredTo]);

  function updateFilters(changes: Record<string, string>, resetPage = true) {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      for (const [key, value] of Object.entries(changes)) {
        if (value) next.set(key, value);
        else next.delete(key);
      }
      if (resetPage) next.delete('page');
      return next;
    }, { replace: true });
  }

  const { data, error, loading, reload } = useResource<Page<Person>>(
    '/api/v1/admin/people',
    {
      role,
      search: debouncedSearch,
      activity: activity || undefined,
      registered_from: registeredFrom || undefined,
      registered_to: registeredTo || undefined,
      sort,
      order,
      page,
      page_size: pageSize,
      user_id: personId || undefined,
      data_purpose: scope,
    },
    [role, debouncedSearch, activity, registeredFrom, registeredTo, sort, order, page, pageSize, personId, scope],
  );

  useRestoreListScroll(`${location.pathname}${location.search}`, !loading && !error && Boolean(data) && search === debouncedSearch);
  const language = i18n.language;
  const invalidAppliedDate = (registeredFrom !== '' && !validCalendarDate(registeredFrom)) ||
    (registeredTo !== '' && !validCalendarDate(registeredTo)) ||
    (registeredFrom !== '' && registeredTo !== '' && registeredFrom > registeredTo);
  const filtered = personId !== '' || role !== 'all' || search !== '' || activity !== '' ||
    registeredFrom !== '' || registeredTo !== '' || draftFrom !== '' || draftTo !== '';
  const dateRangeError = (draftFrom && !validCalendarDate(draftFrom)) ||
    (draftTo && !validCalendarDate(draftTo))
    ? t('people.invalidDateRange')
    : draftFrom && draftTo && draftFrom > draftTo
      ? t('people.reversedDateRange') : null;
  const dateRangeDirty = draftFrom !== registeredFrom || draftTo !== registeredTo;
  const dateRangeLabel = invalidAppliedDate ? t('people.invalidDateRange') : registeredFrom && registeredTo
    ? t('people.betweenDates', { from: formatCalendarDate(registeredFrom, language), to: formatCalendarDate(registeredTo, language) })
    : registeredFrom
      ? t('people.fromDate', { date: formatCalendarDate(registeredFrom, language) })
      : registeredTo
        ? t('people.throughDate', { date: formatCalendarDate(registeredTo, language) })
        : t('people.allDates');

  function applyDateRange() {
    if (dateRangeError || !dateRangeDirty) return;
    updateFilters({ registered_from: draftFrom, registered_to: draftTo });
  }

  useEffect(() => {
    if (search !== debouncedSearch || loading || error || !data || data.page !== page) return;
    const lastPage = Math.max(1, Math.ceil(data.total / pageSize));
    if (page > lastPage) {
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        if (lastPage === 1) next.delete('page');
        else next.set('page', String(lastPage));
        return next;
      }, { replace: true });
    }
  }, [data, debouncedSearch, error, loading, page, pageSize, search, setSearchParams]);

  // Erasure. Typed confirmation rather than a second button: this one takes
  // a month to undo and cannot be undone at all once the month is up.
  async function closeAccount(person: Person) {
    try {
      await request(`/api/v1/admin/people/${person.id}/erasure`, { method: 'POST' });
      toast.success(t('people.erasureRequested'));
      setClosing(null);
      reload();
    } catch {
      toast.failure(t('common.loadFailed'));
    }
  }

  async function reopenAccount(person: Person) {
    try {
      await request(`/api/v1/admin/people/${person.id}/erasure`, { method: 'DELETE' });
      toast.success(t('people.erasureCancelled'));
      reload();
    } catch {
      toast.failure(t('common.loadFailed'));
    }
  }

  // Asked first, both ways. This hands over -- or takes away -- every screen
  // in the dashboard, and it is the one action here that used to happen on a
  // single click while closing an account asks for a typed e-mail address.
  async function toggleAdmin(person: Person) {
    const grant = !person.is_super_admin;
    try {
      await request(`/api/v1/admin/people/${person.id}/super-admin`, {
        method: 'POST',
        query: { grant },
      });
      toast.success(t(grant ? 'people.adminGranted' : 'people.adminRevoked'));
      setPromoting(null);
      reload();
    } catch {
      toast.failure(t('people.cannotRevokeSelf'));
    }
  }

  const columns: Column<Person>[] = [
    {
      key: 'name',
      header: t('people.name'),
      sortKey: 'name',
      render: (person) => (
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <Link className="break-words font-medium text-brand hover:underline" to={personDetailUrl(person.id, `${location.pathname}${location.search}`)} onClick={() => rememberListScroll(`${location.pathname}${location.search}`)}>
              {person.real_name || person.display_name}
            </Link>
            {person.is_super_admin ? (
              <ShieldCheck
                aria-label={t('people.adminAccess')}
                className="h-3.5 w-3.5 shrink-0 text-brand"
              />
            ) : null}
          </div>
          <div className="truncate text-2xs text-ink-subtle">{person.email}</div>
          <div className="mt-1"><PurposeBadge purpose={person.data_purpose} /></div>
          {/* Row 105: outside their squad this athlete is their nickname and
              their code, and those are the two strings somebody reads out on
              the phone. A twelfth column would be past the fold. */}
          {person.nickname || person.player_code ? (
            <div className="truncate text-2xs text-ink-subtle">
              {[person.nickname, person.player_code].filter(Boolean).join(' · ')}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      key: 'role',
      header: t('people.role'),
      sortKey: 'role',
      render: (person) => (
        <div className="flex flex-wrap items-center gap-1">
          {(person.roles?.length ? person.roles : [person.role]).map((identity) => (
            <Badge key={identity} tone={identity === 'coach' ? 'info' : 'neutral'}>
              {t(`people.role${cap(identity)}`)}
            </Badge>
          ))}
          {/* Only when it is not the app: marking the ordinary case would put
              a badge on every row and say nothing. */}
          {person.registration_source && person.registration_source !== 'app' ? (
            <Badge tone="neutral">
              {t(`people.source.${person.registration_source}`, {
                defaultValue: person.registration_source,
              })}
            </Badge>
          ) : null}
        </div>
      ),
    },
    {
      key: 'createdAt',
      header: t('people.createdAt'),
      sortKey: 'created_at',
      align: 'right',
      render: (person) => (
        <span className="text-ink-subtle">{formatBerlinTimestamp(person.created_at, language)}</span>
      ),
    },
    {
      // Kept near the left, after identity and registration. The aftersales work
      // is done from, and with eleven columns the table scrolls sideways --
      // anything past the fold may as well not be there.
      key: 'activity',
      header: t('people.activity'),
      sortKey: 'last_seen_at',
      render: (person) => (
        <div className="flex items-center gap-2">
          <ActivityBadge band={person.activity} />
          <span className="text-2xs text-ink-subtle">
            {relativeDays(person.last_seen_at, language) ?? ''}
          </span>
        </div>
      ),
    },
    {
      key: 'ageGroup',
      header: t('people.ageGroup'),
      sortKey: 'birth_date',
      render: (person) => (
        <span className="text-ink-muted">
          {person.age_group ?? <Missing />}
        </span>
      ),
    },
    {
      key: 'gender',
      header: t('people.gender'),
      sortKey: 'gender',
      render: (person) =>
        person.gender ? (
          <span className="text-ink-muted">{t(`people.gender${cap(person.gender)}`)}</span>
        ) : (
          <Missing />
        ),
    },
    {
      key: 'position',
      header: t('people.position'),
      sortKey: 'position',
      render: (person) =>
        person.field_position ? (
          <span className="text-ink-muted">
            {t(`people.positions.${person.field_position}`, {
              defaultValue: person.field_position,
            })}
          </span>
        ) : (
          <Missing />
        ),
    },
    {
      key: 'country',
      header: t('people.country'),
      sortKey: 'country',
      render: (person) => {
        const code = person.nationality ?? person.country;
        if (!code) return <Missing />;
        return (
          <span className="text-ink-muted">
            <span aria-hidden>{countryFlag(code)} </span>
            {countryName(code, language)}
          </span>
        );
      },
    },
    {
      key: 'club',
      header: t('people.club'),
      sortKey: 'club',
      render: (person) =>
        person.club_name ? (
          <div className="min-w-0">
            <div className="truncate text-ink-muted">{person.club_name}</div>
            {person.teams.length ? (
              <div className="truncate text-2xs text-ink-subtle">
                {person.teams.map((team) => team.name).join(', ')}
              </div>
            ) : null}
          </div>
        ) : (
          <Missing />
        ),
    },
    {
      key: 'subscription',
      header: t('people.appAccess'),
      sortKey: 'status',
      render: (person) => (
        <Badge tone={person.subscription_status === 'vip' ? 'info' : 'neutral'}>
          {t(`subscription.${person.subscription_status}`)}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: t('people.adminAccess'),
      align: 'right',
      render: (person) => (
        <div className="flex justify-end gap-2">
          <Button onClick={() => setEditing(person)} className="text-xs">
            {t('people.baseData')}
          </Button>
          {!person.erased_at && !person.erasure_requested_at && person.role !== 'parent' && <Button
            onClick={() => navigate(`/marketing-consents?athlete=${person.id}`)} className="text-xs">
            {t('marketing.personAction')}
          </Button>}
          {/*
            Row 103: a VIP has to be revocable again, and somebody who never
            redeemed a code has to be able to become one. Finanzen cannot do
            either -- it lists subscriptions, and a person who has never had
            one is not in it. This is the list that holds everybody.
          */}
          <Button onClick={() => setSubscribing(person)} className="text-xs">
            {t('people.editAppAccess')}
          </Button>
          <Button onClick={() => setPurposeEditing(person)} className="text-xs">{t('dataPurpose.edit')}</Button>
          {/*
            Row 110 is about VIPs, and the endpoint answers for nobody else.
            Offering the button on every row would be offering a button that
            fails on all but a handful of them.
          */}
          {person.subscription_status === 'vip' ? (
            <Button onClick={() => setAssigning(person)} className="text-xs">
              {t('people.team')}
            </Button>
          ) : null}
          <Button
            onClick={() => setPromoting(person)}
            disabled={person.id === account?.id}
            className="text-xs"
          >
            {t(person.is_super_admin ? 'people.revokeAdmin' : 'people.grantAdmin')}
          </Button>
          {person.erased_at ? null : person.erasure_requested_at ? (
            <Button onClick={() => reopenAccount(person)} className="text-xs">
              {t('people.erasureCancel')}
            </Button>
          ) : (
            <Button
              onClick={() => setClosing(person)}
              disabled={person.id === account?.id}
              className="text-xs"
            >
              {t('people.erasure')}
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader title={t('people.title')} subtitle={t('people.subtitle')} />

      {personId ? <div className="mb-4 rounded-lg border border-line bg-surface-raised px-4 py-3 text-sm text-ink"><strong className="block">{t('people.selectedAccount')}</strong><span className="text-ink-muted">{data?.items[0] ? `${data.items[0].display_name} · ${data.items[0].email}` : !loading && data ? t('people.selectedAccountMissing') : t('people.selectedAccountLoading')}</span><div className="mt-2 flex flex-wrap gap-3">{data?.items[0] ? <Link className="text-brand hover:underline" to={personDetailUrl(data.items[0].id, returnTo ?? '/people')}>{t('personDetail.viewProfile')}</Link> : null}{returnTo ? <Link className="text-brand hover:underline" to={returnTo}>{t('people.backToClub', { name: returnLabel })}</Link> : null}<button className="text-brand hover:underline" onClick={() => updateFilters({ person_id: '', return_to: '', club_name: '' })}>{t('people.showAllAccounts')}</button></div></div> : null}

      <div className="card">
        <div className="flex flex-wrap items-end gap-3 border-b border-line p-3">
          <div className="relative min-w-[16rem] flex-1">
            <Search
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle"
            />
            <TextInput
              type="search"
              maxLength={120}
              aria-label={t('common.search')}
              placeholder={t('common.searchPlaceholder')}
              className="pl-9"
              value={search}
              onChange={(event) => {
                updateFilters({ search: event.target.value });
              }}
            />
          </div>

          <div className="space-y-1.5">
            <label htmlFor="people-role" className="block text-xs font-medium text-ink">{t('people.role')}</label>
          <Select
            id="people-role"
            aria-label={t('people.role')}
            value={role}
            onChange={(event) => {
              updateFilters({ role: event.target.value === 'all' ? '' : event.target.value });
            }}
          >
            <option value="all">{t('common.all')}</option>
            <option value="athlete">{t('people.roleAthlete')}</option>
            <option value="coach">{t('people.roleCoach')}</option>
            <option value="parent">{t('people.roleParent')}</option>
          </Select>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="people-activity" className="block text-xs font-medium text-ink">{t('people.activity')}</label>
          <Select
            id="people-activity"
            aria-label={t('people.activity')}
            value={activity}
            onChange={(event) => {
              updateFilters({ activity: event.target.value });
            }}
          >
            <option value="">{t('common.all')}</option>
            <option value="active">{t('activity.active')}</option>
            <option value="lapsing">{t('activity.lapsing')}</option>
            <option value="dormant">{t('activity.dormant')}</option>
            <option value="unknown">{t('activity.unknown')}</option>
          </Select>
          </div>

          <PageSizeSelect
            value={pageSize}
            onChange={(next) => {
              setPageSize(next);
              updateFilters({ page: '' }, false);
            }}
          />

          {filtered ? (
            <Button
              onClick={() => {
                setDraftFrom('');
                setDraftTo('');
                setSearchParams(new URLSearchParams(), { replace: true });
              }}
            >
              {t('common.reset')}
            </Button>
          ) : null}
        </div>

        <form
          className="flex flex-wrap items-end gap-3 border-b border-line px-3 py-3"
          onSubmit={(event) => {
            event.preventDefault();
            applyDateRange();
          }}
        >
          <div className="w-44">
            <Field label={t('people.registeredFrom')}>
              {(id, describedBy) => (
                <TextInput
                  id={id}
                  type="date"
                  value={draftFrom}
                  max={draftTo || undefined}
                  aria-describedby={[describedBy, dateRangeError ? 'registration-date-error' : null].filter(Boolean).join(' ') || undefined}
                  invalid={Boolean(dateRangeError)}
                  onChange={(event) => setDraftFrom(event.target.value)}
                />
              )}
            </Field>
          </div>
          <div className="w-44">
            <Field label={t('people.registeredTo')}>
              {(id, describedBy) => (
                <TextInput
                  id={id}
                  type="date"
                  value={draftTo}
                  min={draftFrom || undefined}
                  aria-describedby={[describedBy, dateRangeError ? 'registration-date-error' : null].filter(Boolean).join(' ') || undefined}
                  invalid={Boolean(dateRangeError)}
                  onChange={(event) => setDraftTo(event.target.value)}
                />
              )}
            </Field>
          </div>
          <Button type="submit" variant="primary" disabled={!dateRangeDirty || Boolean(dateRangeError)}>
            {t('people.applyDateRange')}
          </Button>
          <div className="min-w-[14rem] flex-1 text-xs text-ink-subtle">
            <p>{t('people.registrationRangeHint')}</p>
            {dateRangeError ? <p id="registration-date-error" role="alert" className="mt-1 text-danger">{dateRangeError}</p> : null}
          </div>
        </form>

        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-line px-3 py-3" aria-live="polite">
          <strong className="text-lg text-ink">
            {search !== debouncedSearch || loading || !data || error || invalidAppliedDate
              ? '…' : t('people.matchingTotal', { total: count(data.total, language) })}
          </strong>
          <span className="text-xs text-ink-muted">
            {t('people.matchingContext', {
              role: role === 'all' ? t('common.all') : t(`people.role${cap(role)}`),
              range: dateRangeLabel,
            })}
          </span>
        </div>

        <div className="border-b border-line px-3 py-2">
          <ActivityLegend />
        </div>

        {invalidAppliedDate ? (
          <p role="alert" className="px-3 py-8 text-center text-sm text-danger">
            {t('people.invalidDateRange')}
          </p>
        ) : error ? (
          <ErrorState message={t('common.loadFailed')} onRetry={reload} />
        ) : search !== debouncedSearch || loading || !data ? (
          <TableSkeleton columns={7} />
        ) : data && data.items.length === 0 ? (
          <EmptyState
            icon={<Users className="h-8 w-8" />}
            title={t('people.emptyTitle')}
            body={t('people.emptyBody')}
          />
        ) : (
          <>
            <DataTable
              caption={t('people.title')}
              columns={columns}
              rows={data?.items ?? []}
              rowKey={(person) => person.id}
              sort={sort}
              order={order}
              onSort={(key) => {
                updateFilters({ sort: key, order: key === sort && order === 'asc' ? 'desc' : 'asc', page: '' }, false);
              }}
            />
            <Pagination
              page={page}
              pageSize={pageSize}
              total={data?.total ?? 0}
              onPage={(next) => updateFilters({ page: next > 1 ? String(next) : '' }, false)}
            />
          </>
        )}
      </div>

      {/*
        Says outright why coach rows are half empty. Without it the reader
        assumes the dashboard is broken rather than that the data was never
        collected.
      */}
      <p className="mt-3 flex items-start gap-1.5 text-xs text-ink-subtle">
        <Info aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        {t('people.noProfileHint')}
      </p>

      {promoting ? (
        <Modal
          open
          onClose={() => setPromoting(null)}
          title={t(
            promoting.is_super_admin ? 'people.revokeAdminTitle' : 'people.grantAdminTitle',
          )}
          footer={
            <>
              <Button onClick={() => setPromoting(null)}>{t('common.cancel')}</Button>
              <Button
                variant={promoting.is_super_admin ? 'danger' : 'primary'}
                onClick={() => toggleAdmin(promoting)}
              >
                {t(promoting.is_super_admin ? 'people.revokeAdmin' : 'people.grantAdmin')}
              </Button>
            </>
          }
        >
          <p className="text-sm text-ink-muted">
            {t(
              promoting.is_super_admin ? 'people.revokeAdminBody' : 'people.grantAdminBody',
              { name: promoting.real_name || promoting.display_name, email: promoting.email },
            )}
          </p>
        </Modal>
      ) : null}

      {closing ? (
        <Modal
          open
          onClose={() => {
            setClosing(null);
            setTypedEmail('');
          }}
          title={t('people.erasureTitle', { name: closing.real_name || closing.display_name })}
          footer={
            <>
              <Button
                onClick={() => {
                  setClosing(null);
                  setTypedEmail('');
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="danger"
                disabled={typedEmail.trim().toLowerCase() !== closing.email.toLowerCase()}
                onClick={() => closeAccount(closing)}
              >
                {t('people.erasureConfirm')}
              </Button>
            </>
          }
        >
          <p className="text-sm text-ink-muted">{t('people.erasureBody')}</p>
          <Field label={t('people.erasureType', { email: closing.email })}>
            {(id) => (
              <TextInput
                id={id}
                value={typedEmail}
                autoComplete="off"
                onChange={(event) => setTypedEmail(event.target.value)}
              />
            )}
          </Field>
        </Modal>
      ) : null}

      {editing ? (
        <BaseDataDialog
          person={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            toast.success(t('people.baseDataSaved'));
            reload();
          }}
        />
      ) : null}

      {assigning ? (
        <TeamDialog
          person={assigning}
          onClose={() => setAssigning(null)}
          onChanged={(person) => {
            setAssigning(person);
            reload();
          }}
        />
      ) : null}

      {subscribing ? (
        <SubscriptionDialog
          person={subscribing}
          onClose={() => setSubscribing(null)}
          onSaved={() => {
            setSubscribing(null);
            toast.success(t('people.subscriptionSaved'));
            reload();
          }}
        />
      ) : null}
      {purposeEditing ? <PurposeEditor entityType="users" entityId={purposeEditing.id}
        name={purposeEditing.real_name || purposeEditing.display_name} purpose={purposeEditing.data_purpose}
        onClose={() => setPurposeEditing(null)} onSaved={() => { setPurposeEditing(null); reload(); }} /> : null}
    </>
  );
}

/**
 * Row 103: what this person is entitled to, changed by hand and in both
 * directions.
 *
 * Only the status, because that is the whole of the question here. Plan and
 * amount say what somebody is billed, which belongs on the page about money
 * and would be a second place to get it wrong.
 *
 * The note is not sent at all rather than sent empty: a redeemed code writes
 * into it what the code was handed out for, and taking the VIP away should
 * not also take away the record of why it was given.
 */
function SubscriptionDialog({
  person,
  onClose,
  onSaved,
}: {
  person: Person;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [status, setStatus] = useState<SubscriptionStatus>(person.subscription_status);
  const [busy, setBusy] = useState(false);

  const keepsPlan = status !== 'none' && status !== 'vip';

  async function save() {
    setBusy(true);
    try {
      await request(`/api/v1/admin/subscriptions/${person.id}`, {
        method: 'PUT',
        body: {
          status,
          plan: keepsPlan ? person.subscription_plan : null,
        },
      });
      onSaved();
    } catch {
      toast.failure(t('common.loadFailed'));
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t('people.subscriptionTitle', {
        name: person.real_name || person.display_name,
      })}
      description={t('people.subscriptionHint')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={status === person.subscription_status}
            onClick={save}
          >
            {busy ? t('common.saving') : t('common.save')}
          </Button>
        </>
      }
    >
      <Field label={t('finance.status')}>
        {(id) => (
          <Select
            id={id}
            className="w-full"
            value={status}
            onChange={(event) => setStatus(event.target.value as SubscriptionStatus)}
          >
            {(['none', 'trialing', 'active', 'past_due', 'canceled', 'vip'] as const).map(
              (value) => (
                <option key={value} value={value}>
                  {t(`subscription.${value}`)}
                </option>
              ),
            )}
          </Select>
        )}
      </Field>
    </Modal>
  );
}

/**
 * Row 110: a VIP joins a team and leaves it again, and nothing is charged.
 *
 * Stays open across each change so somebody sorting out one person's teams
 * does not have to find the row again between two of them.
 */
function TeamDialog({
  person,
  onClose,
  onChanged,
}: {
  person: Person;
  onClose: () => void;
  onChanged: (person: Person) => void;
}) {
  const { t } = useTranslation();
  const { scope } = useDataScope();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState('');

  const { data: teams } = useResource<AdminTeam[]>('/api/v1/admin/teams', { data_purpose: scope }, [scope]);
  const available = useMemo(
    () =>
      (teams ?? []).filter(
        (team) => !person.teams.some((joined) => joined.id === team.id),
      ),
    [teams, person.teams],
  );

  async function change(run: () => Promise<unknown>, message: string) {
    setBusy(true);
    try {
      const updated = (await run()) as Person;
      onChanged(updated);
      setPicked('');
      toast.success(message);
    } catch {
      toast.failure(t('common.loadFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t('people.teamTitle', {
        name: person.real_name || person.display_name,
      })}
      description={t('people.teamHint')}
      footer={<Button onClick={onClose}>{t('common.close')}</Button>}
    >
      <div className="space-y-4">
        <div>
          <div className="mb-2 text-xs font-medium text-ink-muted">
            {t('people.teamsCurrent')}
          </div>
          {person.teams.length ? (
            <ul className="space-y-2">
              {person.teams.map((team) => (
                <li
                  key={team.id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-line px-3 py-2"
                >
                  <span className="truncate text-sm text-ink">{team.name}</span>
                  <Button
                    className="text-xs"
                    disabled={busy}
                    onClick={() =>
                      change(
                        () =>
                          request(
                            `/api/v1/admin/vips/${person.id}/teams/${team.id}`,
                            { method: 'DELETE' },
                          ),
                        t('people.teamRemoved', { team: team.name }),
                      )
                    }
                  >
                    {t('people.teamRemove')}
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-ink-subtle">{t('people.teamsNone')}</p>
          )}
        </div>

        <Field label={t('people.teamAdd')}>
          {(id) => (
            <div className="flex gap-2">
              <Select
                id={id}
                className="w-full"
                value={picked}
                onChange={(event) => setPicked(event.target.value)}
              >
                <option value="">{t('people.teamPick')}</option>
                {available.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.club_name ? `${team.club_name} · ${team.name}` : team.name}
                  </option>
                ))}
              </Select>
              <Button
                variant="primary"
                loading={busy}
                disabled={!picked}
                onClick={() =>
                  change(
                    () =>
                      request(`/api/v1/admin/vips/${person.id}/teams`, {
                        method: 'POST',
                        body: { team_id: picked },
                      }),
                    t('people.teamAdded'),
                  )
                }
              >
                {t('people.teamAssign')}
              </Button>
            </div>
          )}
        </Field>
      </div>
    </Modal>
  );
}

function BaseDataDialog({
  person,
  onClose,
  onSaved,
}: {
  person: Person;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const [realName, setRealName] = useState(person.real_name ?? '');
  const [anonymous, setAnonymous] = useState(person.board_anonymous);
  const [birthDate, setBirthDate] = useState(person.birth_date ?? '');
  const [gender, setGender] = useState(person.gender ?? '');
  const [location, setLocation] = useState(person.location ?? '');
  const [country, setCountry] = useState(person.country ?? '');
  const [nationality, setNationality] = useState(person.nationality ?? '');

  const { data: codes } = useResource<string[]>('/api/v1/admin/countries', {}, []);
  // Sorted by the name the reader sees, not by the code behind it: by code the
  // list opens Andorra, United Arab Emirates, Afghanistan.
  const countries = useMemo(() => {
    const language = i18n.language;
    return (codes ?? [])
      .map((code) => ({ code, name: countryName(code, language) ?? code }))
      .sort((a, b) => a.name.localeCompare(b.name, language));
  }, [codes, i18n.language]);

  async function save() {
    setBusy(true);
    try {
      await request(`/api/v1/admin/people/${person.id}/profile`, {
        method: 'PATCH',
        body: {
          real_name: realName.trim() || null,
          birth_date: birthDate || null,
          gender: gender || null,
          location: location.trim() || null,
          country: country || null,
          nationality: nationality || null,
          board_anonymous: anonymous,
        },
      });
      onSaved();
    } catch {
      toast.failure(t('common.loadFailed'));
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t('people.baseDataTitle', {
        name: person.real_name || person.display_name,
      })}
      description={t('people.baseDataHint')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" loading={busy} onClick={save}>
            {busy ? t('common.saving') : t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t('people.realName')}>
          {(id) => (
            <TextInput
              id={id}
              value={realName}
              maxLength={100}
              onChange={(event) => setRealName(event.target.value)}
            />
          )}
        </Field>

        <Field label={t('people.birthDate')}>
          {(id) => (
            <TextInput
              id={id}
              type="date"
              value={birthDate}
              onChange={(event) => setBirthDate(event.target.value)}
            />
          )}
        </Field>

        <Field label={t('people.gender')}>
          {(id) => (
            <Select
              id={id}
              className="w-full"
              value={gender}
              onChange={(event) => setGender(event.target.value)}
            >
              <option value="">{t('common.none')}</option>
              <option value="male">{t('people.genderMale')}</option>
              <option value="female">{t('people.genderFemale')}</option>
              <option value="other">{t('people.genderOther')}</option>
            </Select>
          )}
        </Field>

        <Field label={t('people.residence')} hint={t('people.residenceHint')}>
          {(id, describedBy) => (
            <CountrySelect
              id={id}
              describedBy={describedBy}
              value={country}
              onChange={setCountry}
              options={countries}
              noneLabel={t('common.none')}
            />
          )}
        </Field>

        <Field label={t('people.nationality')} hint={t('people.nationalityHint')}>
          {(id, describedBy) => (
            <CountrySelect
              id={id}
              describedBy={describedBy}
              value={nationality}
              onChange={setNationality}
              options={countries}
              noneLabel={t('common.none')}
            />
          )}
        </Field>

        <Field label={t('people.location')}>
          {(id) => (
            <TextInput
              id={id}
              value={location}
              maxLength={200}
              onChange={(event) => setLocation(event.target.value)}
            />
          )}
        </Field>

        {person.role === 'athlete' ? (
          /* Row 117: the objection is the player's, but the person asking for
             it is usually a parent on the phone. */
          <Field label={t('people.boardAnonymous')} hint={t('people.boardAnonymousHint')}>
            {() => (
              <Toggle
                checked={anonymous}
                onChange={setAnonymous}
                label={t('people.boardAnonymous')}
              />
            )}
          </Field>
        ) : null}

        {person.role === 'coach' ? (
          <p className="text-xs text-ink-subtle">{t('people.positionAthleteOnly')}</p>
        ) : null}
      </div>
    </Modal>
  );
}

function CountrySelect({
  id,
  describedBy,
  value,
  onChange,
  options,
  noneLabel,
}: {
  id: string;
  describedBy?: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ code: string; name: string }>;
  noneLabel: string;
}) {
  return (
    <Select
      id={id}
      aria-describedby={describedBy}
      className="w-full"
      value={value}
      onChange={(event) => onChange(event.target.value)}
    >
      <option value="">{noneLabel}</option>
      {/*
        A country already on the row but missing from the list still has to
        show, or opening the form and saving it would quietly clear it.
      */}
      {value && !options.some((option) => option.code === value) ? (
        <option value={value}>{value}</option>
      ) : null}
      {options.map((option) => (
        <option key={option.code} value={option.code}>
          {option.name}
        </option>
      ))}
    </Select>
  );
}

function Missing() {
  return (
    <span aria-hidden className="text-ink-subtle">
      —
    </span>
  );
}

function cap(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function validCalendarDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

function formatCalendarDate(value: string, language: string): string {
  return new Intl.DateTimeFormat(language.startsWith('de') ? 'de-DE' : 'en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC',
  }).format(new Date(`${value}T00:00:00Z`));
}

function formatBerlinTimestamp(value: string, language: string): string {
  // API timestamps without an offset represent UTC, not the viewer's local time.
  const timestamp = /[Zz]|[+-]\d{2}:?\d{2}$/.test(value) ? value : `${value}Z`;
  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) return '—';
  return new Intl.DateTimeFormat(language.startsWith('de') ? 'de-DE' : 'en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Europe/Berlin',
  }).format(parsed);
}
